import { KeyValue } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  finalize,
  fromEvent,
  map,
} from 'rxjs';
import { RfpService } from '../../views/layout/rfpcontract/service/rfpservice.service';

interface SheetCache {
  headers: string[];
  sheetData: any[];
  sheetColumn: any[];
  maxOfColumns: number;
}

@Component({
  standalone: false,
  selector: 'ngx-excel-viewer',
  templateUrl: 'ngx-excel-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['ngx-excel-viewer.component.scss'],
})
export class NgxExcelViewerComponent
  implements OnInit, OnChanges, AfterContentInit
{
  // ─── Inputs ───────────────────────────────────────────────────────────────
  @Input() id: string = '';
  @Input() isRepo: boolean = false;
  @Input() data: any;
  @Input() sheetNames: string[] = [];
  @Input() editable: boolean = false;
  @Input() events: BehaviorSubject<any> | null = null;
  @Input() saveDataOutput: 'complete' | 'edited-only' = 'complete';
  @Input() sheetDetail: any;
  @Input() documentDetails: any;
  @Input() rfpDocumentsListData!: any[];
  @Input() selectedFileVersionId!: number;
  @Input() module: string = '';
  @Input() readOnly: boolean = false;

  // ─── Outputs ──────────────────────────────────────────────────────────────
  @Output() onDataSave = new EventEmitter<any>();
  @Output() onScrollEnd = new EventEmitter<any>();
  @Output() onSheetChange = new EventEmitter<string>();

  // ─── ViewChild ────────────────────────────────────────────────────────────
  @ViewChild('textArea') textArea!: ElementRef<HTMLTextAreaElement>;

  // ─── Public template-bound state ──────────────────────────────────────────
  public currentSheetName = '';
  public maxOfColumns = 0;
  public selectedCellIndices: [number, number] = [-1, -1];
  public headers: string[] = [];
  public sheetData: any[] = [];
  public sheetColumn: any[] = [];
  public isSheetDataChanged = false;
  public modifiedRows: number[] = [];
  public loading = false;

  question_field: any;
  answer_field: any;
  starting_row: any;
  comments_field: any;
  attachment_field: any;
  editcell: any[] = [null, null];
  editEnabled: boolean = false;
  saveEnabled: boolean = false;
  excelSheetEdited: any[] = [];
  extractcell: any[] = [null, null];
  extractList: any = new Map();
  public docId!: number;
  public rfpDocs!: any[];

  sheetFieldMap = new Map<
    string,
    {
      question_field: string | null;
      answer_field: string | null;
      comments_field: string | null;
      attachment_field: string | null;
      starting_row: number;
    }
  >();

  // ─── Private ──────────────────────────────────────────────────────────────
  private backupSheetData: any[] = [];
  private renderedPages = 1;
  private lastScrollTop = 0;
  private readonly sheetCache = new Map<string, SheetCache>();

  constructor(
    private readonly rfpService: RfpService,
    private readonly cdr: ChangeDetectorRef, // to manually trigger change detection
    private readonly ngZone: NgZone, // to control when CD runs during switch
  ) {}

  get activeFields() {
    return (
      this.sheetFieldMap.get(this.currentSheetName) ?? {
        question_field: null,
        answer_field: null,
        comments_field: null,
        attachment_field: null,
        starting_row: 0,
      }
    );
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  ngOnInit(): void {
    if (this.data != null) {
      this.sheetNames = Object.keys(this.data);
      this.currentSheetName = this.sheetNames[0];
      this.buildAllSheetCache();
      this.applySheet(this.currentSheetName);
    }

    if (this.events != null) {
      this.events.subscribe((event) => {
        if (event.id !== this.id) return;

        if (event.type === 'cancel') {
          this.sheetData = JSON.parse(JSON.stringify(this.backupSheetData));
          this.isSheetDataChanged = false;
          this.modifiedRows = [];
          this.cdr.detectChanges();
        } else if (event.type === 'save') {
          if (this.saveDataOutput === 'complete') {
            this.onDataSave.emit({
              id: this.id,
              sheet: this.currentSheetName,
              data: this.sheetData.map((row: any) => row['value']),
            });
            return;
          }
          const payload: any = {};
          for (const rowIndex of this.modifiedRows) {
            payload[`${rowIndex + 1}`] = this.sheetData[rowIndex].map(
              (row: any) => row['value'],
            );
          }
          this.onDataSave.emit({
            id: this.id,
            sheet: this.currentSheetName,
            data: payload,
          });
        } else if (event.type === 'saved') {
          for (const rowIndex of this.modifiedRows) {
            this.data[rowIndex] = this.sheetData[rowIndex].map(
              (row: any) => row['value'],
            );
            this.backupSheetData[rowIndex] = this.sheetData[rowIndex];
          }
        } else if (event.type === 'new_data') {
          this.run();
        } else if (event.type === 'switchSheet') {
          if (event.sheet && this.sheetNames.includes(event.sheet)) {
            this.setSelectedSheetName(event.sheet);
          }
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['data'] &&
      changes['data'].previousValue !== changes['data'].currentValue
    ) {
      this.run();
    }
    if (changes['selectedFileVersionId']?.currentValue) {
      this.docId = changes['selectedFileVersionId'].currentValue;
    }
    if (changes['rfpDocumentsListData']?.currentValue) {
      this.rfpDocs = changes['rfpDocumentsListData'].currentValue;
    }
    if (changes['sheetDetail'] || changes['documentDetails']) {
      this.setSheetEditable();
    }
  }

  ngAfterContentInit(): void {
    this.setSheetEditable();
  }

  // ─── Cache ────────────────────────────────────────────────────────────────

  /**
   * Pre-computes headers, columns, and data references for every sheet once
   * when data arrives. Switching sheets becomes an O(1) Map lookup.
   */
  private buildAllSheetCache(): void {
    this.sheetCache.clear();
    for (const sheetName of this.sheetNames) {
      this.sheetCache.set(sheetName, this.computeSheetEntry(sheetName));
    }
  }

  private computeSheetEntry(sheetName: string): SheetCache {
    const rows: any[] = this.data[sheetName];
    if (!rows?.length) {
      return { headers: [], sheetData: [], sheetColumn: [], maxOfColumns: 0 };
    }
    const sheetColumn = Object.keys(rows[0]);
    const maxOfColumns = sheetColumn.length > 5 ? sheetColumn.length - 1 : 10;
    const headers = this.computeHeaders(maxOfColumns);
    return { headers, sheetData: rows, sheetColumn, maxOfColumns };
  }

  private computeHeaders(max: number): string[] {
    const headers: string[] = [];
    for (let i = 0; i <= max; i++) {
      let header = '';
      let num = i;
      while (num >= 0) {
        header = String.fromCharCode(65 + (num % 26)) + header;
        num = Math.floor(num / 26) - 1;
      }
      headers.push(header);
    }
    return headers;
  }

  /**
   * Assigns cached values to template-bound properties.
   * Uses spread [...] to give Angular a new array reference,
   * ensuring the *ngFor re-renders even if row objects are the same.
   */
  private applySheet(sheetName: string): boolean {
    const cached = this.sheetCache.get(sheetName);
    if (!cached) return false;
    this.headers = [...cached.headers];
    this.sheetData = [...cached.sheetData]; // new ref = CD sees change
    this.sheetColumn = [...cached.sheetColumn];
    this.maxOfColumns = cached.maxOfColumns;
    return true;
  }

  // ─── run() — only when @Input data changes ────────────────────────────────

  run(): void {
    if (!this.data) return;
    this.sheetNames = Object.keys(this.data);
    if (
      !this.currentSheetName ||
      !this.sheetNames.includes(this.currentSheetName)
    ) {
      this.currentSheetName = this.sheetNames[0];
    }
    this.buildAllSheetCache();
    this.applySheet(this.currentSheetName);
    this.cdr.detectChanges();
  }

  // ─── Sheet switching ──────────────────────────────────────────────────────

  setSelectedSheetName(sheet: string): void {
    if (this.isSheetDataChanged) {
      alert('Save sheet before switching');
      return;
    }

    // ── Phase 1: show spinner ──────────────────────────────────────────────
    this.editcell = [null, null];
    this.editEnabled = false;
    this.currentSheetName = sheet;
    this.sheetData = []; // clear old rows immediately
    this.loading = true;
    this.cdr.detectChanges(); // ← paint spinner NOW (synchronous)

    // ── Phase 2: load content after browser has painted spinner ───────────
    setTimeout(() => {
      this.applySheet(sheet);
      this.loading = false;
      this.onSheetChange.emit(sheet);
      this.cdr.detectChanges(); // ← paint new content
    }, 0);
  }

  // ─── Cell interaction ─────────────────────────────────────────────────────

  selectCell(i: number, j: number): void {
    if (
      (this.selectedCellIndices[0] === i &&
        this.selectedCellIndices[1] === j) ||
      i > this.sheetData.length - 1 ||
      j > this.maxOfColumns - 1
    ) {
      (
        document.querySelector(
          `#input-${this.selectedCellIndices[0]}-${this.selectedCellIndices[1]}`,
        ) as any
      )?.focus();
      return;
    }
    this.selectedCellIndices = [Math.max(0, i), Math.max(0, j)];
    (document.querySelector(`#input-${i}-${j}`) as any)?.focus();
  }

  onDataChanges(event: any, i: number, j: number): void {
    if (!this.editable) return;
    if (this.backupSheetData[i]?.[j]?.['value'] !== event.target.innerText) {
      this.sheetData[i][j]['value'] = event.target.innerText;
      if (!this.modifiedRows.includes(i)) this.modifiedRows.push(i);
      this.isSheetDataChanged = true;
    }
  }

  onInputChange(event: Event, row: any, column: any): void {
    const value = (event.target as HTMLTextAreaElement).value;
    this.sheetData[row][this.sheetColumn[column]] = value;
    this.data[this.currentSheetName] = this.sheetData;
    // Keep cache in sync so navigating away/back preserves the edit.
    const cached = this.sheetCache.get(this.currentSheetName);
    if (cached) cached.sheetData = this.sheetData;
    this.saveEnabled = true;
  }

  enableEdit(i: any, j: any): void {
    this.editcell = [i, j];
    this.editEnabled = true;
    this.cdr.detectChanges();

    const isQuestionField = this.activeFields.question_field == this.headers[j];

    setTimeout(() => {
      if (this.isRepo) {
        const el = document.getElementById(`input-${i}-${j}`);
        if (el && !isQuestionField) {
          el.focus();
          const range = document.createRange();
          const sel = window.getSelection();
          range.selectNodeContents(el);
          range.collapse(false);
          sel?.removeAllRanges();
          sel?.addRange(range);
        }
        // repo + question field stays locked, unchanged
      } else if (this.textArea?.nativeElement) {
        const el = this.textArea.nativeElement;
        const height =
          el.parentElement?.parentElement?.getBoundingClientRect().height ??
          100;
        el.focus();
        el.selectionStart = el.value.length;
        el.style.height = height + 'px';
        this.captureedit();
      }
    }, 0);
  }

  onRepoEditBlur(event: FocusEvent, i: number, j: number): void {
    if (this.activeFields.question_field == this.headers[j]) {
      return;
    }
    const value = (event.target as HTMLElement).innerText?.trim();

    this.sheetData[i][this.sheetColumn[j]] = value;
    this.data[this.currentSheetName] = this.sheetData;

    const cached = this.sheetCache.get(this.currentSheetName);
    if (cached) cached.sheetData = this.sheetData;

    this.saveEnabled = true;

    const cellId = this.headers[j] + '' + (i + 1);
    const answerField = this.activeFields.answer_field;

    // ── NEW: read actual question text from the question_field column ──
    const questionFieldHeader = this.activeFields.question_field;
    const questionColIndex = questionFieldHeader
      ? this.headers.indexOf(questionFieldHeader)
      : -1;
    const actualQuestion =
      questionColIndex !== -1
        ? (this.sheetData[i][this.sheetColumn[questionColIndex]] ?? '')
        : '';

    const newEntry = {
      cell: cellId,
      question: value,
      extract: false,
      actual_question: actualQuestion,
    };

    if (this.excelSheetEdited.length === 0) {
      this.excelSheetEdited.push({
        sheet_name: this.currentSheetName,
        answer_field: answerField,
        sheet_data: [newEntry],
      });
      return;
    }

    const sheetEntry = this.excelSheetEdited.find(
      (s) => s['sheet_name'] === this.currentSheetName,
    );

    if (!sheetEntry) {
      this.excelSheetEdited.push({
        sheet_name: this.currentSheetName,
        answer_field: answerField,
        sheet_data: [newEntry],
      });
      return;
    }

    const cellEntry = sheetEntry['sheet_data'].find(
      (c: any) => c['cell'] === cellId,
    );
    if (cellEntry) {
      cellEntry['question'] = value;
      cellEntry['actual_question'] = actualQuestion; // ── NEW
    } else {
      sheetEntry['sheet_data'].push(newEntry);
    }
  }

  // ─── Keyboard / scroll ────────────────────────────────────────────────────

  @HostListener('keypress', ['$event'])
  onKeyPress(event: any): void {
    if (event.which === 13) event.preventDefault();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: any): void {
    if (!event.key?.includes('Arrow')) return;

    // ── NEW: if user is editing a cell (repo div or textarea), let the
    //         browser handle arrow keys natively — don't intercept.
    if (this.editEnabled && this.editcell[0] !== null) return;

    let i = this.selectedCellIndices[0];
    let j = this.selectedCellIndices[1];
    event.target.blur();
    if (event.key === 'ArrowUp') i--;
    else if (event.key === 'ArrowDown') i++;
    else if (event.key === 'ArrowLeft') {
      if (window.getSelection()?.anchorOffset === 0) j--;
    } else if (event.key === 'ArrowRight') {
      if (
        (window.getSelection()?.anchorOffset ?? 0) >=
        event.target.innerText.length
      )
        j++;
    }
    this.selectCell(i, j);
  }

  onScroll(event: any): void {
    if (event.target.scrollTop > this.lastScrollTop) {
      if (
        event.target.offsetHeight + event.target.scrollTop >=
        event.target.scrollHeight
      ) {
        this.onScrollEnd.emit({
          id: this.id,
          pageNo: this.renderedPages++,
          sheet: this.currentSheetName,
        });
      }
    }
    this.lastScrollTop = event.target.scrollTop;
  }

  // ─── Edit capture ─────────────────────────────────────────────────────────

  captureedit(): void {
    const input$ = fromEvent<Event>(this.textArea.nativeElement, 'input').pipe(
      map((e) => (e.target as HTMLInputElement).value),
      debounceTime(1000),
      distinctUntilChanged(),
    );
    input$.subscribe((val: any) => this.generateEditData(val));
  }

  generateEditData(editValue: any): void {
    const cellId =
      this.headers[this.editcell[1]] + '' + (parseInt(this.editcell[0]) + 1);
    const answerField = this.activeFields.answer_field;

    // ── NEW: read actual question text from the question_field column ──
    const questionFieldHeader = this.activeFields.question_field;
    const questionColIndex = questionFieldHeader
      ? this.headers.indexOf(questionFieldHeader)
      : -1;
    const actualQuestion =
      questionColIndex !== -1
        ? (this.sheetData[this.editcell[0]][
            this.sheetColumn[questionColIndex]
          ] ?? '')
        : '';

    const newEntry = {
      cell: cellId,
      question: editValue,
      extract: false,
      actual_question: actualQuestion,
    };

    if (this.excelSheetEdited.length === 0) {
      this.excelSheetEdited.push({
        sheet_name: this.currentSheetName,
        answer_field: answerField,
        sheet_data: [newEntry],
      });
      return;
    }

    const sheetIndex = this.excelSheetEdited.findIndex(
      (s) => s['sheet_name'] === this.currentSheetName,
    );

    if (sheetIndex === -1) {
      this.excelSheetEdited.push({
        sheet_name: this.currentSheetName,
        answer_field: this.activeFields.answer_field,
        sheet_data: [newEntry],
      });
      return;
    }

    const sheetEntry = this.excelSheetEdited[sheetIndex];
    const questionIndex = sheetEntry['sheet_data'].findIndex(
      (c: any) => c['cell'] === cellId,
    );

    if (questionIndex !== -1) {
      sheetEntry['sheet_data'][questionIndex]['question'] = editValue;
      sheetEntry['sheet_data'][questionIndex]['actual_question'] =
        actualQuestion; // ── NEW
    } else {
      sheetEntry['sheet_data'].push(newEntry);
    }
  }

  // ─── AI extract / toggle ──────────────────────────────────────────────────

  async toggle(
    event: any,
    i: any,
    j: any,
    data: any,
    SheetName: string,
    datas?: any,
  ): Promise<void> {
    this.saveEnabled = true;
    let answerField = this.activeFields.answer_field;
    this.extractcell = [i, j];
    const cellId =
      this.headers[this.extractcell[1]] +
      '' +
      (parseInt(this.extractcell[0]) + 1);

    const questionFieldHeader = this.activeFields.question_field;
    const questionColIndex = questionFieldHeader
      ? this.headers.indexOf(questionFieldHeader)
      : -1;
    const actualQuestion =
      questionColIndex !== -1
        ? (this.sheetData[i][this.sheetColumn[questionColIndex]] ?? '')
        : '';

    if (event.target.checked) {
      //  only add if not already in list ──
      if (this.extractList.has(SheetName)) {
        if (!this.extractList.get(SheetName).includes(cellId)) {
          this.extractList.get(SheetName).push(cellId);
        }
      } else {
        this.extractList.set(SheetName, [cellId]);
      }
      answerField = await this.getExtractFromAnswerAi(i, j).toPromise();
      this.markasExtract(
        cellId,
        data,
        false,
        SheetName,
        answerField,
        actualQuestion,
      );
    } else {
      // remove cellId from extractList on uncheck ──
      if (this.extractList.has(SheetName)) {
        const list = this.extractList.get(SheetName);
        const idx = list.indexOf(cellId);
        if (idx > -1) list.splice(idx, 1);
      }
      this.markasExtract(
        cellId,
        data,
        false,
        SheetName,
        answerField,
        actualQuestion,
      );
    }
  }

  getExtractFromAnswerAi(i: any, j: any) {
    const payload: any = { questions: [this.sheetData[i][j]] };

    if (typeof this.selectedFileVersionId === 'number') {
      const result = this.getDocumentByDetailsId(this.selectedFileVersionId);
      if (result) Object.assign(payload, result);
    }

    this.loading = true;
    this.cdr.detectChanges();

    const service = this.rfpService;

    return service.getexcelAnswerFromAi(payload).pipe(
      map((data: any) => {
        if (data?.result?.length) {
          const answerColumnIndex = this.headers.indexOf(
            this.activeFields.answer_field!,
          );
          const answer = data.result[0]?.answer;
          this.sheetData[i][answerColumnIndex] = answer;
          const cached = this.sheetCache.get(this.currentSheetName);
          if (cached) cached.sheetData = [...this.sheetData];
          return answer;
        }
        return null;
      }),
      finalize(() => {
        this.loading = false;
        this.cdr.detectChanges();
      }),
    );
  }

  ischecked(i: any, j: any, SheetName: string): boolean {
    const cellId = this.headers[j] + '' + (parseInt(i) + 1);
    if (!this.extractList.has(SheetName)) return false;
    return this.extractList.get(SheetName).includes(cellId);
  }

  markasExtract(
    cellId: any,
    data: any,
    extract: boolean,
    SheetName: string,
    answer: any,
    actualQuestion: string = '',
  ): void {
    const question = {
      cell: cellId,
      question: answer,
      extract,
      actual_question: actualQuestion,
    };

    if (this.excelSheetEdited.length === 0) {
      this.excelSheetEdited.push({
        sheet_name: this.currentSheetName,
        answer_field: this.activeFields.answer_field,
        sheet_data: [question],
      });
      return;
    }

    const sheetEntry = this.excelSheetEdited.find(
      (d: any) => d['sheet_name'] === SheetName,
    );
    if (!sheetEntry) {
      this.excelSheetEdited.push({
        sheet_name: this.currentSheetName,
        answer_field: this.answer_field,
        sheet_data: [question],
      });
      return;
    }

    const cellEntry = sheetEntry['sheet_data'].find(
      (c: any) => c['cell'] === cellId,
    );
    if (!cellEntry) {
      sheetEntry['sheet_data'].push(question);
    } else {
      cellEntry['extract'] = extract;
      cellEntry['actual_question'] = actualQuestion;
    }
  }

  // ─── Document helpers ─────────────────────────────────────────────────────

  getDocumentByDetailsId(id: string | number): {
    doc_id: number;
    client_id: number;
    kb_doc_ids: string | string[] | null;
  } | null {
    if (!Array.isArray(this.rfpDocumentsListData)) return null;
    const doc = this.rfpDocumentsListData.find((item) => item?.id === id);
    if (!doc) return null;
    return {
      doc_id: doc.id,
      client_id: doc.client_id,
      kb_doc_ids:
        doc?.metadata?.rfp_type_id?.toLowerCase() === 'all'
          ? 'all'
          : (doc.kb_doc_ids ?? null),
    };
  }

  // ─── Sheet editable config ────────────────────────────────────────────────

  setSheetEditable(): void {
    this.sheetFieldMap.clear();
    const isCreatedRepo =
      this.documentDetails?.status === 'Created' &&
      this.documentDetails?.type === 'repository';
    const isTrained = this.documentDetails?.type === 'trained';

    if (!isCreatedRepo && !isTrained) return;
    if (!this.sheetDetail?.sheets?.length) return;

    for (const sheetConfig of this.sheetDetail.sheets) {
      const sheetName = sheetConfig['sheet_name'];
      if (isCreatedRepo) {
        this.sheetFieldMap.set(sheetName, {
          question_field: sheetConfig['question_field']?.toUpperCase() ?? null,
          answer_field: sheetConfig['answer_field']?.toUpperCase() ?? null,
          comments_field: sheetConfig['comments_field']?.toUpperCase() ?? null,
          attachment_field:
            sheetConfig['attachments_field']?.toUpperCase() ?? null,
          starting_row: sheetConfig['starting_row'],
        });
      } else {
        this.sheetFieldMap.set(sheetName, {
          question_field: sheetConfig['question_column']?.toUpperCase() ?? null,
          answer_field: sheetConfig['answer_column']?.toUpperCase() ?? null,
          comments_field: sheetConfig['comments_column']?.toUpperCase() ?? null,
          attachment_field:
            sheetConfig['attachment_column']?.toUpperCase() ?? null,
          starting_row: sheetConfig['starting_row'],
        });
      }
    }
  }

  // ─── Emit actions ─────────────────────────────────────────────────────────

  Update(): void {
    this.onDataSave.emit({
      data: this.excelSheetEdited,
      action: 'update',
      editedQuestion:
        this.excelSheetEdited[0]?.sheet_data?.[0]?.actual_question ?? '',
      editedAnswer: this.excelSheetEdited[0]?.sheet_data?.[0]?.question ?? '',
    });
  }

  SaveNew(): void {
    this.onDataSave.emit({
      data: this.excelSheetEdited,
      action: 'savenew',
      editedQuestion:
        this.excelSheetEdited[0]?.sheet_data?.[0]?.actual_question ?? '',
      editedAnswer: this.excelSheetEdited[0]?.sheet_data?.[0]?.question ?? '',
    });
  }

  UpdateTrain(): void {
    this.onDataSave.emit({
      data: this.excelSheetEdited,
      action: 'updateTrain',
    });
  }

  changeStatus(status: any): void {
    if (status === 'reviewed') {
      this.onDataSave.emit({ data: this.excelSheetEdited, action: 'approved' });
    } else if (status === 'submitted') {
      this.onDataSave.emit({
        data: this.excelSheetEdited,
        action: 'submitted',
      });
    }
  }

  // ─── Misc ─────────────────────────────────────────────────────────────────

  checkColumn(data: any): string {
    return /^column.*\d$/i.test(data) ? '' : data;
  }

  keepOrder(x: KeyValue<string, any>, y: KeyValue<string, any>): any {
    return 1;
  }

  // ─── trackBy helpers (used in template ngFor) ─────────────────────────────

  /**
   * Track rows and columns by their index.
   * Prevents Angular from destroying/recreating DOM nodes that haven't moved.
   * Critical for large sheets — without this every sheet switch rebuilds the
   * entire table from scratch even if the row count is the same.
   */
  trackByIndex(index: number): number {
    return index;
  }

  /**
   * Track header columns by their string value (e.g. "A", "B", "AA").
   * Headers don't change between sheets of the same column count, so Angular
   * can skip recreating those <th> elements entirely.
   */
  trackByHeader(index: number, header: string): string {
    return header;
  }

  // ─── Add New Row ──────────────────────────────────────────────────────────

  addNewRow(): void {
    if (!this.currentSheetName || !this.sheetColumn.length) return;

    // Build an empty row matching the current sheet's column structure
    const emptyRow: any = {};
    for (const col of this.sheetColumn) {
      emptyRow[col] = '';
    }

    // Push to sheetData and sync back to data source + cache
    this.sheetData = [...this.sheetData, emptyRow];
    this.data[this.currentSheetName] = this.sheetData;

    const cached = this.sheetCache.get(this.currentSheetName);
    if (cached) cached.sheetData = this.sheetData;

    // Mark the new row index as modified so it's included in save
    const newRowIndex = this.sheetData.length - 1;
    if (!this.modifiedRows.includes(newRowIndex)) {
      this.modifiedRows.push(newRowIndex);
    }

    this.saveEnabled = true;
    this.isSheetDataChanged = true;
    this.cdr.detectChanges();

    // Auto-enable edit on the first editable cell of the new row
    setTimeout(() => {
      const firstEditableColIndex = this.headers.indexOf(
        this.activeFields.question_field ?? this.headers[0],
      );
      this.enableEdit(newRowIndex, Math.max(0, firstEditableColIndex));

      // Scroll new row into view
      const rows = document.querySelectorAll('.excel-table-data tbody tr');
      rows[newRowIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 50);
  }

  deleteRow(rowIndex: number): void {
    this.sheetData = this.sheetData.filter((_, i) => i !== rowIndex);
    this.data[this.currentSheetName] = this.sheetData;

    const cached = this.sheetCache.get(this.currentSheetName);
    if (cached) cached.sheetData = this.sheetData;

    // Recalculate modifiedRows after deletion
    this.modifiedRows = this.modifiedRows
      .filter((r) => r !== rowIndex)
      .map((r) => (r > rowIndex ? r - 1 : r));

    this.saveEnabled =
      this.modifiedRows.length > 0 || this.sheetData.length > 0;
    this.isSheetDataChanged = true;
    this.cdr.detectChanges();
  }

  onRepoEditInput(): void {
    if (
      this.editEnabled &&
      this.activeFields.question_field == this.headers[this.editcell[1]]
    ) {
      return;
    }
    this.saveEnabled = true;
  }

  isCheckedModel(i: any, j: any, sheetName: string): boolean {
    return this.ischecked(i, j, sheetName);
  }

  onToggleChange(
    event: any,
    i: any,
    j: any,
    cell: any,
    sheetName: string,
    data: any,
  ): void {
    this.toggle(event, i, j, cell, sheetName, data);
  }

  onLockedKeydown(event: KeyboardEvent, i: number, j: number): void {
    if (this.activeFields.question_field == this.headers[j]) {
      // Allow navigation/selection keys, block everything that mutates content.
      const allowed = [
        'Tab',
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
        'Escape',
      ];
      if (
        (!allowed.includes(event.key) && !(event.ctrlKey || event.metaKey)) ||
        ((event.ctrlKey || event.metaKey) &&
          !['c', 'a'].includes(event.key.toLowerCase()))
      ) {
        event.preventDefault();
      }
    }
  }

  onLockedPaste(event: ClipboardEvent, i: number, j: number): void {
    if (this.activeFields.question_field == this.headers[j]) {
      event.preventDefault();
    }
  }
}
